import React, { useState, useMemo, useEffect } from 'react';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Avatar } from 'primereact/avatar';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { FilterMatchMode } from 'primereact/api';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { GitLabProject, ProjectTechnology } from '../types/gitlab';
import { useGitLab } from '../context/GitLabContext';

interface ProjectSelectorProps {
  selectedProjects: GitLabProject[];
  onSelectionChange: (projects: GitLabProject[]) => void;
  suggestedProjectIds?: number[];
  isLoadingSuggestions?: boolean;
}

const ProjectSelector: React.FC<ProjectSelectorProps> = ({
  selectedProjects,
  onSelectionChange,
  suggestedProjectIds = [],
  isLoadingSuggestions = false,
}) => {
  const { projects, isLoading, refreshProjects } = useGitLab();
  const [globalFilter, setGlobalFilter] = useState('');
  const [hasAutoSelected, setHasAutoSelected] = useState(false);

  // Auto-select projects based on suggested project IDs from GitLab search
  useEffect(() => {
    console.log('[ProjectSelector] Auto-select check:', {
      hasAutoSelected,
      isLoadingSuggestions,
      suggestedProjectIdsCount: suggestedProjectIds.length,
      projectsCount: projects.length,
      selectedProjectsCount: selectedProjects.length,
      suggestedProjectIds,
    });

    if (
      !hasAutoSelected &&
      !isLoadingSuggestions &&
      suggestedProjectIds.length > 0 &&
      projects.length > 0 &&
      selectedProjects.length === 0
    ) {
      // Match projects by ID (much simpler and more reliable)
      const matchedProjects = projects.filter((project) =>
        suggestedProjectIds.includes(project.id)
      );

      console.log(`[ProjectSelector] Found ${matchedProjects.length} matching projects:`, matchedProjects.map(p => p.path_with_namespace));

      if (matchedProjects.length > 0) {
        onSelectionChange(matchedProjects);
        setHasAutoSelected(true);
      }
    }
  }, [
    suggestedProjectIds,
    projects,
    selectedProjects.length,
    isLoadingSuggestions,
    hasAutoSelected,
    onSelectionChange,
  ]);

  const filters: DataTableFilterMeta = useMemo(() => ({
    global: { value: globalFilter, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    'namespace.name': { value: null, matchMode: FilterMatchMode.CONTAINS },
  }), [globalFilter]);

  const projectTemplate = (project: GitLabProject) => (
    <div className="flex align-items-center gap-3">
      <Avatar
        image={project.avatar_url || undefined}
        icon={!project.avatar_url ? 'pi pi-folder' : undefined}
        shape="circle"
        size="normal"
        className="project-avatar"
      />
      <div>
        <div className="font-semibold text-color">{project.name}</div>
        <div className="text-color-secondary text-sm">{project.path_with_namespace}</div>
      </div>
    </div>
  );

  const versionTemplate = (project: GitLabProject) => {
    if (!project.versionInfo) {
      return (
        <span className="text-color-secondary text-sm">
          <i className="pi pi-spin pi-spinner mr-2" />{' '}Loading...
        </span>
      );
    }

    const { type, value, hasUatBranch } = project.versionInfo;
    
    // Handle special cases
    if (value === 'No releases') {
      return (
        <Tag
          value="No releases"
          severity="secondary"
          icon="pi pi-minus-circle"
        />
      );
    }
    
    if (value === 'Error') {
      return (
        <Tag
          value="Error"
          severity="danger"
          icon="pi pi-exclamation-triangle"
        />
      );
    }
    
    if (type === 'tag') {
      return (
        <div className="flex flex-column gap-1">
          <Tag
            value={value}
            severity="success"
            icon="pi pi-tag"
          />
        </div>
      );
    } else {
      // Commit from main branch (has UAT)
      const truncatedValue = value.length > 30 ? value.substring(0, 30) + '...' : value;
      return (
        <div className="flex flex-column gap-1">
          <Tag
            value={truncatedValue}
            severity="info"
            icon="pi pi-code"
            className="version-commit-tag"
          />
          {hasUatBranch && (
            <Tag
              value="UAT"
              severity="warning"
              icon="pi pi-flag"
              className="text-xs"
            />
          )}
        </div>
      );
    }
  };

  const technologyTemplate = (project: GitLabProject) => {
    if (!project.technology) {
      return (
        <span className="text-color-secondary text-sm">
          <i className="pi pi-spin pi-spinner mr-2" />{' '}...
        </span>
      );
    }

    const techConfig: Record<ProjectTechnology, { icon: string; color: string; bgColor: string }> = {
      'React': { icon: '⚛️', color: '#61DAFB', bgColor: 'rgba(97, 218, 251, 0.15)' },
      'Next.js': { icon: '▲', color: '#ffffff', bgColor: 'rgba(255, 255, 255, 0.15)' },
      'Node.js': { icon: '🟢', color: '#68A063', bgColor: 'rgba(104, 160, 99, 0.15)' },
      'Python': { icon: '🐍', color: '#3776AB', bgColor: 'rgba(55, 118, 171, 0.15)' },
      'Java': { icon: '☕', color: '#ED8B00', bgColor: 'rgba(237, 139, 0, 0.15)' },
      'Unknown': { icon: '❓', color: '#888888', bgColor: 'rgba(136, 136, 136, 0.15)' },
    };

    const config = techConfig[project.technology];

    return (
      <span 
        className="technology-badge"
        style={{ 
          background: config.bgColor, 
          color: config.color,
          padding: '0.25rem 0.75rem',
          borderRadius: '1rem',
          fontSize: '0.85rem',
          fontWeight: 500,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.4rem',
        }}
      >
        <span>{config.icon}</span>
        <span>{project.technology}</span>
      </span>
    );
  };

  const activityTemplate = (project: GitLabProject) => {
    const date = new Date(project.last_activity_at);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    let severity: 'success' | 'info' | 'warning' | 'danger' = 'success';
    if (diffDays > 30) severity = 'warning';
    if (diffDays > 90) severity = 'danger';

    return (
      <Tag
        value={diffDays === 0 ? 'Today' : `${diffDays}d ago`}
        severity={severity}
      />
    );
  };

  const actionsTemplate = (project: GitLabProject) => (
    <Button
      icon="pi pi-external-link"
      className="p-button-text p-button-rounded p-button-sm"
      tooltip="Open in GitLab"
      tooltipOptions={{ position: 'left' }}
      onClick={(e) => {
        e.stopPropagation();
        window.open(project.web_url, '_blank');
      }}
    />
  );

  const header = (
    <div className="flex flex-column gap-3">
      <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3">
        <div className="flex align-items-center gap-2">
          <i className="pi pi-folder-open text-xl text-primary"></i>
          <span className="font-semibold text-lg">
            Select Projects ({selectedProjects.length} selected)
          </span>
          {isLoadingSuggestions && (
            <span className="text-color-secondary text-sm flex align-items-center gap-2">
              <ProgressSpinner style={{ width: '16px', height: '16px' }} strokeWidth="4" />
              Loading suggestions from Jira...
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <IconField iconPosition="left">
            <InputIcon className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search projects..."
              className="w-full md:w-20rem"
            />
          </IconField>
          <Button
            icon="pi pi-refresh"
            className="p-button-outlined"
            tooltip="Refresh projects"
            tooltipOptions={{ position: 'left' }}
            onClick={refreshProjects}
            loading={isLoading}
          />
        </div>
      </div>
      {hasAutoSelected && suggestedProjectIds.length > 0 && (
        <Message
          severity="info"
          text={`${selectedProjects.length} project${selectedProjects.length === 1 ? '' : 's'} auto-selected based on GitLab commits/MRs. You can modify the selection.`}
          className="w-full"
        />
      )}
    </div>
  );

  return (
    <div className="project-selector">
      <DataTable
        value={projects}
        selection={selectedProjects}
        onSelectionChange={(e) => onSelectionChange(e.value as GitLabProject[])}
        dataKey="id"
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        filters={filters}
        globalFilterFields={['name', 'path_with_namespace', 'namespace.name', 'description']}
        header={header}
        emptyMessage="No projects found"
        loading={isLoading}
        selectionMode="multiple"
        className="project-table"
        scrollable
        scrollHeight="calc(100vh - 400px)"
        stripedRows
        sortField="last_activity_at"
        sortOrder={-1}
      >
        <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
        <Column
          header="Project"
          body={projectTemplate}
          sortable
          sortField="name"
          style={{ minWidth: '200px' }}
        />
        <Column
          header="Tech"
          body={technologyTemplate}
          sortable
          sortField="technology"
          style={{ minWidth: '90px' }}
        />
        <Column
          header="Last Version"
          body={versionTemplate}
          style={{ minWidth: '140px' }}
        />
        <Column
          header="Activity"
          body={activityTemplate}
          sortable
          sortField="last_activity_at"
          style={{ minWidth: '80px' }}
        />
        <Column
          body={actionsTemplate}
          headerStyle={{ width: '3rem' }}
        />
      </DataTable>
    </div>
  );
};

export default ProjectSelector;
